import { Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  AgentArchitecture,
  AssistantKey,
  ChatMessage,
  ChatMessageRole,
  ListTestCasesByAssistantResponse,
  SortDirection,
  TestCase,
  TestType,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { Observable, of, timer } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import {
  GalaxyColumnDef,
  GalaxyDataSource,
  GalaxyTableModule,
  PagedListRequestInterface,
  PagedResponseInterface,
} from '@vendasta/galaxy/table';
import { map, switchMap } from 'rxjs/operators';
import { MatTableModule } from '@angular/material/table';
import { TestCaseEditModalComponent } from '../test-case-edit-modal/test-case-edit-modal.component';
import {
  EvaluateSourceModalComponent,
  EvaluateSourceModalData,
  EvaluateSourceModalResult,
} from '../evaluate-source-modal/evaluate-source-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { TestManagementService } from '../test-management.service';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { getAgentArchitectureName, isVoiceTest, snackbarDuration } from '../test-run.utils';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'ai-test-case-display',
  imports: [
    MatButtonModule,
    MatIconModule,
    GalaxyTableModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    AiAssistantI18nModule,
    GalaxyTooltip,
    MatSlideToggleModule,
  ],

  templateUrl: './test-case-display.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './test-case-display.component.scss',
})
export class TestCaseDisplayComponent implements OnInit {
  private readonly testService = inject(TestManagementService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(SnackbarService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  @Input() assistantKey: AssistantKey;
  @Input() testType: TestType = TestType.TEST_TYPE_CHAT;
  @Input() title = 'Test Cases';
  onlyEvaluators = false;
  @Output() readonly runTests: EventEmitter<string[]> = new EventEmitter();
  @Output() readonly configureTestRun: EventEmitter<void> = new EventEmitter();
  @Output() readonly uploadTestCases: EventEmitter<void> = new EventEmitter();
  @Output() readonly generateTestCases: EventEmitter<void> = new EventEmitter();

  selectedRows: TestCase[] = [];

  dataSource: GalaxyDataSource<TestCase, void, MatSort> = {} as GalaxyDataSource<TestCase, void, MatSort>;
  columns: GalaxyColumnDef[] = [];

  private chatColumns: GalaxyColumnDef[] = [
    { id: 'select' },
    { id: 'evaluator', title: '' },
    { id: 'name', title: 'Name', sortable: true },
    { id: 'prompt', title: 'Prompt' },
    { id: 'expectation', title: 'Expectation' },
    { id: 'agentArchitecture', title: 'Agent Architecture' },
    { id: 'edit', title: '', pinned: true },
    { id: 'delete', title: '', pinned: true },
  ];

  private voiceColumns: GalaxyColumnDef[] = [
    { id: 'select' },
    { id: 'evaluator', title: '' },
    { id: 'name', title: 'Name', sortable: true },
    { id: 'prompt', title: 'Prompt' },
    { id: 'expectation', title: 'Expectation' },
    { id: 'edit', title: '', pinned: true },
    { id: 'delete', title: '', pinned: true },
  ];

  get isVoice(): boolean {
    return this.testType === TestType.TEST_TYPE_VOICE;
  }

  selectionChanged(rows: TestCase[]) {
    this.selectedRows = rows;
  }

  runSelectedTests(): void {
    this.runTests.emit(this.selectedRows.map((tc) => tc.id));
  }

  sortData(sort: Sort): void {
    this.dataSource?.setSorting([{ active: sort.active, direction: sort.direction } as MatSort]);
  }

  toggleOnlyEvaluators(value: boolean): void {
    this.onlyEvaluators = value;
    this.dataSource?.setFilters([]);
  }

  openEvaluateSourceModal(): void {
    this.dialog
      .open<EvaluateSourceModalComponent, EvaluateSourceModalData, EvaluateSourceModalResult>(
        EvaluateSourceModalComponent,
        {
          data: { kind: this.isVoice ? 'call-record' : 'conversation' },
          width: '560px',
        },
      )
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) return of(null);
          return this.testService.runSuccessEvaluation(this.assistantKey, result);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.snackbar.openSuccessSnack(
              this.translateService.instant('AI_ASSISTANT.TESTING.EVALUATE_MODAL.STARTED'),
              { duration: snackbarDuration },
            );
          }
        },
        error: (error) => {
          const message = error.error?.message
            ? error.error.message
            : this.translateService.instant('AI_ASSISTANT.TESTING.EVALUATE_MODAL.ERROR');
          this.snackbar.openErrorSnack(message, { duration: snackbarDuration });
        },
      });
  }

  ngOnInit(): void {
    this.columns = this.isVoice ? this.voiceColumns : this.chatColumns;
    this.dataSource = new GalaxyDataSource<TestCase, void, MatSort>(this);
    this.dataSource.setSorting([{ active: 'name', direction: 'asc' } as MatSort]);
  }

  editTestCase(testCase: TestCase | null): void {
    let editing: Partial<TestCase> | null = testCase;
    if (!editing) {
      const emptyMessage = {
        role: ChatMessageRole.CHAT_MESSAGE_ROLE_USER,
        content: '',
        contentParts: [],
        externalId: '',
      } as ChatMessage;
      editing = {
        id: '',
        name: '',
        expectation: '',
        testType: this.testType,
        chatHistory: this.isVoice ? [] : [emptyMessage],
        expectedToolCalls: [],
        agentArchitecture: 'UNSET',
        assistantKey: this.assistantKey,
        callerInstructions: '',
        expectedCitations: [],
        isSuccessEvaluator: false,
        scenario: '',
      };
    }

    this.dialog
      .open(TestCaseEditModalComponent, {
        data: {
          testCase: editing,
          testType: this.testType,
        },
      })
      .afterClosed()
      .pipe(
        switchMap((result: Partial<TestCase>) => {
          if (result) {
            return this.testService.upsertTestCases(this.assistantKey, [result]).pipe(map(() => true));
          }
          return of(null);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.snackbar.openSuccessSnack(this.translateService.instant('AI_ASSISTANT.TESTING.TEST_CASES.SAVED'), {
              duration: snackbarDuration,
            });
            this.refreshDataSource();
          }
        },
        error: (error) => {
          const message = error.error?.message
            ? error.error.message
            : this.translateService.instant('AI_ASSISTANT.TESTING.TEST_CASES.ERROR_SAVING');
          this.snackbar.openErrorSnack(message, { duration: snackbarDuration });
        },
      });
  }

  deleteTestCase(testCase: TestCase): void {
    this.confirmationModal
      .openModal({
        title: this.translateService.instant('AI_ASSISTANT.TESTING.TEST_CASES.DELETE_TITLE'),
        message: this.translateService.instant('AI_ASSISTANT.TESTING.TEST_CASES.DELETE_CONFIRM'),
        confirmButtonText: this.translateService.instant('AI_ASSISTANT.SHARED.DELETE'),
        type: 'warn',
      })
      .pipe(
        switchMap((result: boolean) => {
          if (result) {
            return this.testService.deleteTestCases(this.assistantKey, [testCase.id]).pipe(map(() => true));
          }
          return of(false);
        }),
      )
      .subscribe({
        next: (deleted) => {
          if (deleted) {
            this.snackbar.openSuccessSnack(this.translateService.instant('AI_ASSISTANT.TESTING.TEST_CASES.DELETED'), {
              duration: snackbarDuration,
            });
            this.refreshDataSource();
          }
        },
        error: (error) => {
          const message = error.error?.message
            ? error.error.message
            : this.translateService.instant('AI_ASSISTANT.TESTING.TEST_CASES.ERROR_DELETING');
          this.snackbar.openErrorSnack(message, { duration: snackbarDuration });
        },
      });
  }

  refresh(): void {
    this.dataSource.state = {
      ...this.dataSource.state,
      shouldRefetch: true,
      loading: true,
    };
  }

  private refreshDataSource(): void {
    this.dataSource.state = {
      ...this.dataSource.state,
      loading: true,
    };

    timer(1500).subscribe(() => {
      this.dataSource.state = {
        ...this.dataSource.state,
        shouldRefetch: true,
        loading: true,
      };
    });
  }

  get(r: PagedListRequestInterface<void, MatSort>): Observable<PagedResponseInterface<TestCase>> {
    let sortDirection: SortDirection = SortDirection.SORT_DIRECTION_ASCENDING;
    if (r.sorting?.length > 0 && r.sorting[0].direction === 'desc') {
      sortDirection = SortDirection.SORT_DIRECTION_DESCENDING;
    }
    return this.testService
      .listTestCasesByAssistant(
        this.assistantKey,
        r.pagingOptions,
        r.searchOptions?.text,
        sortDirection,
        this.onlyEvaluators,
      )
      .pipe(
        map((resp: ListTestCasesByAssistantResponse) => {
          const allCases = resp.testCases || [];
          const filtered = allCases.filter((tc) =>
            this.isVoice ? isVoiceTest(tc.testType) : !isVoiceTest(tc.testType),
          );
          return {
            data: filtered,
            pagingMetadata: resp.pagingMetadata,
          };
        }),
      );
  }

  getAgentArchitectureName(architecture: AgentArchitecture): string {
    return getAgentArchitectureName(architecture, this.translateService);
  }
}
